import React from "react";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { createUseStyles } from "react-jss";
import ButtonLarge1 from "components/elements/ButtonLarge1";
import ButtonMedium1 from "components/elements/ButtonMedium1";
import BrandConsentPopup from "components/blocks/consent/BrandConsentPopup";
import CookiePopUp from "components/blocks/cookiePopUp/CookiePopUp";
import GdprPopUp from "components/blocks/consent/Gdpr";
import CustomizedDialogs from "components/elements/CustomizedDialogs";
import ContentExtraSmall from "components/elements/ContentExtraSmall";
import ContentLarge from "components/elements/ContentLarge";
import ContentNormal from "components/elements/ContentNormal";
import Headline2 from "components/elements/Headline2";
import LanguageSelector from "components/blocks/LanguageSelector/LanguageSelector";
import UnderMaintenance from "./underMaintenance";
import Translate, { translate } from "utils/Translate";
import { textStyles } from "styles/styles";
import AuthPage from "./AuthPage";

const useStyles = createUseStyles({
  layout: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 1,
    width: "100%",
    margin: 0,
  },
  formColumn: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "0 1 calc(var(--auth-login-width) + 24px)",
    maxWidth: "100%",
    padding: 12,
  },
  cookieColumn: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "fixed",
    top: "50%",
    right: "max(24px, calc((100vw - 1400px) / 2))",
    zIndex: 3,
    width: "calc(var(--auth-cookie-width) + 24px)",
    maxWidth: "100%",
    padding: 12,
    transform: "translateY(-50%)",
  },
  formContent: {
    width: "100%",
    maxWidth: "var(--auth-login-width)",
    padding: "var(--auth-card-padding)",
    borderRadius: 28,
    backgroundColor: "#fff",
    boxShadow: "0 24px 70px rgba(30, 45, 64, 0.14)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "var(--auth-section-gap)",
  },
  logo: {
    objectFit: "contain",
    maxWidth: "70%",
    maxHeight: "var(--auth-logo-height)",
    height: "auto",
  },
  intro: {
    margin: "var(--auth-section-gap) 0",
    color: "#5c6673",
  },
  brandIntro: {
    marginTop: 8,
    marginBottom: "var(--auth-section-gap)",
    color: "#5c6673",
  },
  input: {
    minHeight: 44,
    padding: "var(--auth-input-padding) 16px",
    border: "1px solid #d6dce3",
    borderRadius: 10,
    fontSize: 16,
  },
  requestError: {
    margin: "0 0 16px",
    padding: "14px 16px",
    border: "1px solid #f0c6c3",
    borderLeft: "4px solid #c8423b",
    borderRadius: 10,
    backgroundColor: "#fff7f6",
    color: "#71312d",
  },
  requestErrorTitle: {
    margin: "0 0 4px",
    fontWeight: 600,
  },
  requestErrorMessage: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.5,
  },
  identifierField: {
    position: "relative",
  },
  identifierInputWithAction: {
    paddingRight: 88,
  },
  identifierAction: {
    position: "absolute",
    top: "50%",
    right: 16,
    transform: "translateY(-50%)",
  },
  codeGroup: {
    marginTop: 22,
  },
  sendCodeRow: {
    margin: "0 0 12px",
  },
  resendArea: {
    marginTop: 14,
    textAlign: "center",
    color: "#687482",
    fontSize: 14,
  },
  resendAction: {
    display: "inline-block",
    padding: "2px 4px",
    border: 0,
    background: "transparent",
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "underline",
    cursor: "pointer",
    "&:disabled": {
      opacity: 0.6,
      cursor: "default",
    },
  },
  textButton: {
    padding: 0,
    border: 0,
    background: "none",
    color: "#26384a",
    fontWeight: 600,
    textDecoration: "underline",
    cursor: "pointer",
  },
  identifierSummary: {
    marginTop: 8,
    color: "#687482",
    fontSize: 14,
    textAlign: "center",
  },
  actionRow: {
    margin: "20px 0 0",
  },
  secondaryLinks: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "8px 24px",
    marginTop: "var(--auth-section-gap)",
  },
  link: {
    color: "#465567",
    cursor: "pointer",
    fontSize: 14,
    textAlign: "center",
    textDecoration: "none",
  },
  banner: {
    marginTop: "var(--auth-section-gap)",
    color: "#687482",
  },
  "@media screen and (max-width: 1359px) and (min-height: 801px), screen and (max-width: 1139px)": {
    cookieColumn: {
      position: "absolute",
      top: "calc(100% + 16px)",
      right: "auto",
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
  "@media screen and (max-width: 575px)": {
    formContent: {
      borderRadius: 22,
    },
    formColumn: { padding: 0 },
  },
});

const NewLoginPage = ({
  brandData,
  styleGuide,
  title,
  description,
  logo,
  loginPageBrandLogoHeight,
  loginPageBrandLogoWidth,
  isDownForMaintenance,
  shutDownAllBrands,
  browserNotSupport,
  isCookieAccepted,
  setCookieAccepted,
  userIdentifier,
  handleLoginInput,
  accessCode,
  setAccessCode,
  accessCodeField,
  identifierInvalid,
  identifierError,
  handleIdentifierKeyPress,
  grant,
  resendCountdown,
  codeRequestIsLoading,
  codeRequestError,
  changeIdentifier,
  requestCode,
  handleAccessCodeKeyPress,
  errorCode,
  submitCode,
  loginIsLoading,
  loginProblemModal,
  setLoginProblemModal,
  showNewUserModal,
  setShowNewUserModal,
  success,
  setSuccess,
  handleRequestCodeKeyPress,
  showGDPRConsent,
  acceptGDPR,
  closeGdpr,
  showBrandConsent,
  brandTermsUrl,
  acceptBrandConsent,
  closeBrandConsent,
}) => {
  const classes = useStyles();
  const features = brandData?.features || {};
  const brandSlug = brandData?.brandSlug;
  const contactEmail = brandData?.contactEmail;
  const contactPhone = brandData?.contactPhone;
  const email = contactEmail || (brandSlug ? `support+${brandSlug}@upgrader.club` : "support@upgrader.club");

  React.useEffect(() => {
    if (grant) accessCodeField.current?.focus();
  }, [grant, accessCodeField]);

  return (
    <AuthPage>
      {(isDownForMaintenance || shutDownAllBrands) ? (
        <div style={{ width: "100%" }}>
          <UnderMaintenance />
        </div>
      ) : (
        <>
          {browserNotSupport && <CustomizedDialogs />}
          {!isCookieAccepted && (
            <CookiePopUp inline callback={setCookieAccepted} />
          )}
          <Container fluid className="p-0">
            <Row className={classes.layout}>
              <Col xs={12} lg="auto" className={classes.formColumn}>
                <main className={`${classes.formContent} auth-card`}>
                  <header className={classes.header}>
                    {logo && (
                      <img
                        alt="Brand Logo"
                        className={classes.logo}
                        width={loginPageBrandLogoWidth || "auto"}
                        height={loginPageBrandLogoHeight || 60}
                        src={logo}
                      />
                    )}
                    <LanguageSelector />
                  </header>
                  <Headline2
                    style={{ fontSize: "var(--auth-title-size)", lineHeight: 1.2 }}
                    fallbackText={title || translate("_LOGIN_PAGE_LOGIN_TEXT_", "Log ind")}
                  />
                  {description && (
                    <div className={classes.brandIntro}>
                      <ContentNormal style={{ whiteSpace: "pre-line", fontSize: "var(--auth-login-copy-size)", lineHeight: 1.5 }}>{description}</ContentNormal>
                    </div>
                  )}
                  <div className={classes.intro}>
                    <ContentNormal style={{ fontSize: "var(--auth-login-body-text-size)", lineHeight: 1.5 }}>
                      {grant
                        ? translate("_LOGIN_CODE_SENT_MESSAGE_", "Indtast engangskoden, vi har sendt til:")
                        : <Translate translationKey="_LOGIN_ENTER_CREDENTIAL_MESSAGE_">
                            Indtast dit mobilnummer eller din e-mail. Så sender vi en
                            engangskode, du kan logge ind med.
                          </Translate>}
                    </ContentNormal>
                  </div>
                  <Form autoComplete="off" onSubmit={(event) => event.preventDefault()}>
                    <Form.Group className="mb-3" controlId="email">
                      <div className={classes.identifierField}>
                        <Form.Control
                          value={userIdentifier}
                          className={`${classes.input} ${grant ? classes.identifierInputWithAction : ""}`}
                          style={{ fontFamily: styleGuide.secondaryFontFamily }}
                          type="text"
                          placeholder={translate("_LOGIN_INPUT_EMAIL_PLACEHOLDER_", "E-mail eller telefon nummer")}
                          onChange={handleLoginInput}
                          disabled={!isCookieAccepted || grant || browserNotSupport}
                          onKeyPress={handleIdentifierKeyPress}
                        />
                        {grant && (
                          <button
                            type="button"
                            className={`${classes.textButton} ${classes.identifierAction}`}
                            onClick={changeIdentifier}
                            disabled={codeRequestIsLoading}
                          >
                            {translate("_CHANGE_", "Ændr")}
                          </button>
                        )}
                      </div>
                      {identifierInvalid && identifierError}
                    </Form.Group>
                    {codeRequestError && (
                      <div className={classes.requestError} role="alert" aria-live="polite">
                        <p className={classes.requestErrorTitle}>
                          {translate("_LOGIN_CODE_REQUEST_ERROR_TITLE_", "Vi kunne ikke sende en engangskode")}
                        </p>
                        <p className={classes.requestErrorMessage}>
                          {getCodeRequestErrorMessage(codeRequestError)}
                        </p>
                      </div>
                    )}
                    <Row className={classes.sendCodeRow}>
                      {!grant && (
                        <ButtonMedium1
                          showIcon={false}
                          style={{ width: "100%", justifyContent: "center", fontFamily: styleGuide.secondaryFontFamily }}
                          disabled={!isCookieAccepted || codeRequestIsLoading}
                          fallbackText="Send kode"
                          translationKey="_LOGIN_BUTTON_SENDCODE_TEXT_"
                          loading={codeRequestIsLoading}
                          onClick={(event) => requestCode(event, true)}
                        />
                      )}
                    </Row>
                    {grant && (
                      <>
                    <Form.Group className={classes.codeGroup} controlId="formHorizontalEmail">
                      <Form.Control
                        id="invalidPassword"
                        className={classes.input}
                        style={{ fontFamily: styleGuide.secondaryFontFamily }}
                        type="text"
                        name="one-time-code"
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        maxLength={6}
                        value={accessCode}
                        placeholder={translate("_LOGIN_INPUT_CODE_PLACEHOLDER_", "Engangskode")}
                        onChange={(event) => setAccessCode(event.target.value)}
                        onKeyPress={handleAccessCodeKeyPress}
                        ref={accessCodeField}
                        disabled={!isCookieAccepted || !grant}
                      />
                      {errorCode === 401 && (
                        <p className="mt-2 mb-0 text-danger">
                          {translate("_WRONG_CODE_ENTERED_", "Forkert kode indtastet - prøv igen")}
                        </p>
                      )}
                      {errorCode === 429 && (
                        <p className="mt-2 mb-0 text-danger">
                          {translate(
                            "_MAXIMUM_ATTEMPT_REACHED_",
                            "Kode er indtastet forkert 3 gange. Du skal bede om ny kode ved at indtaste dine informationer igen"
                          )}
                        </p>
                      )}
                    </Form.Group>
                    <div className={classes.resendArea}>
                      {resendCountdown > 0 ? (
                        translate("_LOGIN_RESEND_CODE_COUNTDOWN_", `Send en ny kode om ${resendCountdown} sek.`)
                      ) : (
                        <button
                          type="button"
                          className={classes.resendAction}
                          style={{ color: styleGuide.color16, fontFamily: styleGuide.secondaryFontFamily }}
                          disabled={!isCookieAccepted || codeRequestIsLoading}
                          onClick={(event) => requestCode(event, true, true)}
                        >
                          {codeRequestIsLoading
                            ? translate("_SENDING_CODE_", "Sender kode...")
                            : translate("_LOGIN_BUTTON_SENDCODE_AGAIN_TEXT_", "Send kode igen")}
                        </button>
                      )}
                    </div>
                    <Row className={classes.actionRow}>
                      <Col>
                        <ButtonLarge1
                          style={{ width: "100%", fontFamily: styleGuide.secondaryFontFamily }}
                          onClick={submitCode}
                          fallbackText="Log ind"
                          translationKey="_LOGIN_BUTTON_LOGIN_TEXT_"
                          disabled={!isCookieAccepted || !grant}
                          loading={loginIsLoading}
                        />
                      </Col>
                    </Row>
                    </>
                    )}
                    <div className={classes.secondaryLinks}>
                      <button
                        type="button"
                        className={`btn btn-link p-0 ${classes.link}`}
                        onClick={() => setLoginProblemModal(true)}
                      >
                        {translate("_LOGIN_PROBLEM_TEXT_", "Problemer med at logge ind?")}
                      </button>
                      {!!features.loginPageShowNewUserButton && (
                        <button
                          type="button"
                          className={`btn btn-link p-0 ${classes.link}`}
                          onClick={() => setShowNewUserModal(true)}
                        >
                          {translate("_LOGIN_NEW_USER_TEXT_", "Opret ny bruger")}
                        </button>
                      )}
                      {features.registerUser && (
                        <Link className={classes.link} to="/register">
                          {translate("_CREATE_NEW_USER_ACCOUNT_", "Opret ny bruger")}
                        </Link>
                      )}
                    </div>
                  </Form>
                  {features.loginPageBottomBannerText && (
                    <div className={classes.banner}>
                      <ContentExtraSmall>{features.loginPageBottomBannerText}</ContentExtraSmall>
                    </div>
                  )}
                </main>
              </Col>
            </Row>
          </Container>
          <Modal show={loginProblemModal} onHide={() => setLoginProblemModal(false)} centered>
            <Modal.Header className="pb-0 border-0" closeButton />
            <Modal.Body className="pt-0">
              <p style={textStyles.headline5}>{translate("_LOGIN_PROBLEM_TEXT_", "Problemer med at logge ind?")}</p>
              <p style={textStyles.contentNormal}>
                {translate(
                  "_LOGIN_PROBLEM_CHECK_SPAM_MESSAGE_",
                  "Kontrollér dit spamfilter, hvis du har bedt om koden på e-mail. Hvis du stadig ikke kan logge ind kan du kontakte support på: "
                )}
              </p>
              <p style={{ ...textStyles.contentNormal, marginBottom: 0 }}>
                Mail: {email}
                <br />
                {translate("_TELEPHONE_", "Telefon")}: {contactPhone || "+45 31 15 84 11"}
              </p>
            </Modal.Body>
          </Modal>
          <Modal show={showNewUserModal} onHide={() => setShowNewUserModal(false)} centered>
            <Modal.Header className="pb-0 border-0" closeButton />
            <Modal.Body className="pt-0">
              <p style={textStyles.headline5}>{translate("_LOGIN_NEW_USER_TEXT_", "Opret ny bruger")}</p>
              <p style={textStyles.contentNormal}>
                {translate("_LOGIN_NEW_USER_POPUP_TEXT_", "Er du ny bruger og ønsker adgang, bedes du kontakte os.")}
              </p>
              <p style={{ ...textStyles.contentNormal, marginBottom: 0 }}>
                Mail: {email}
                <br />
                {contactPhone ? `${translate("_TELEPHONE_", "Telefon")}: ${contactPhone}` : ""}
              </p>
            </Modal.Body>
          </Modal>
          <Form onKeyPress={handleRequestCodeKeyPress}>
            <Modal show={success} onHide={() => setSuccess(false)} centered>
              <Modal.Header className="pb-0 border-0" closeButton />
              <Modal.Body className="pt-0">
                <ContentLarge fallbackText="Din kode er afsendt" translationKey="_SEND_CODE_SUCESS_TEXT_" />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  style={{ ...buttonStyles, fontFamily: styleGuide.secondaryFontFamily, backgroundColor: styleGuide.color16 }}
                  onClick={() => setSuccess(false)}
                >
                  {translate("_BUTTON_CAPTION_CLOSE_", "Luk")}
                </Button>
              </Modal.Footer>
            </Modal>
          </Form>
          {showGDPRConsent && <GdprPopUp accept={acceptGDPR} close={closeGdpr} />}
          {showBrandConsent && (
            <BrandConsentPopup termsUrl={brandTermsUrl} accept={acceptBrandConsent} close={closeBrandConsent} />
          )}
        </>
      )}
    </AuthPage>
  );
};

const getCodeRequestErrorMessage = (error) => {
  const message = typeof error === "string"
    ? error
    : error?.error || error?.message || "";
  const normalizedMessage = String(message).trim().toLowerCase();

  if (["no users", "user not found"].includes(normalizedMessage)) {
    return translate(
      "_LOGIN_CODE_REQUEST_NO_ACCOUNT_",
      "Vi fandt ingen konto med den e-mail eller det telefonnummer. Tjek stavning og landekode, eller prøv den oplysning, du brugte, da du blev oprettet."
    );
  }
  if (normalizedMessage === "not unique user") {
    return translate(
      "_LOGIN_CODE_REQUEST_MULTIPLE_ACCOUNTS_",
      "Vi fandt flere konti med de oplysninger. Kontakt support via “Problemer med at logge ind?” nedenfor, så hjælper vi dig."
    );
  }
  if (normalizedMessage === "brand is not active") {
    return translate(
      "_LOGIN_CODE_REQUEST_BRAND_INACTIVE_",
      "Klubben er midlertidigt lukket for login. Kontakt klubbens support, hvis du har brug for hjælp."
    );
  }
  if (normalizedMessage === "brand not found") {
    return translate(
      "_LOGIN_CODE_REQUEST_BRAND_NOT_FOUND_",
      "Klubben kunne ikke findes. Kontrollér, at du er på den rigtige klubs hjemmeside, eller kontakt support."
    );
  }
  if (normalizedMessage === "user identifier invalid") {
    return translate(
      "_LOGIN_CODE_REQUEST_INVALID_IDENTIFIER_",
      "Indtast den e-mailadresse eller det telefonnummer, der er knyttet til din konto."
    );
  }
  return translate(
    "_LOGIN_CODE_REQUEST_GENERIC_ERROR_",
    "Prøv igen om lidt. Hvis problemet fortsætter, kontakt support via “Problemer med at logge ind?” nedenfor."
  );
};

const buttonStyles = {
  backgroundColor: "#cc3333",
  border: "none",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: 14,
};

export default NewLoginPage;
